import { useCallback, useEffect, useState } from 'react'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'

/**
 * Notificaciones en el teléfono.
 *
 * La campana solo sirve con la aplicación abierta. Esto es lo otro: el aviso
 * llega aunque el técnico tenga el teléfono en el bolsillo y la aplicación
 * cerrada, porque lo entrega el sistema operativo y no la pestaña.
 *
 * SE PIDE EL PERMISO A MANO Y NUNCA AL ENTRAR. Un navegador que pregunta por
 * notificaciones apenas se abre una página recibe un «no» casi siempre —y ese
 * «no» es para siempre—. Se pregunta cuando la persona toca «Activar», que es
 * cuando ya sabe para qué.
 */

const LLAVE_PUBLICA = import.meta.env.VITE_VAPID_PUBLICA as string | undefined

/**
 * La llave va en base64url y el navegador la pide en bytes.
 *
 * Se construye sobre un ArrayBuffer explícito: TypeScript tipa el búfer de un
 * Uint8Array como ArrayBufferLike —que incluye SharedArrayBuffer— y
 * applicationServerKey solo acepta ArrayBuffer.
 */
function aBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const relleno = '='.repeat((4 - (base64url.length % 4)) % 4)
  const base64 = (base64url + relleno).replace(/-/g, '+').replace(/_/g, '/')
  const crudo = atob(base64)

  const bytes = new Uint8Array(new ArrayBuffer(crudo.length))
  for (let i = 0; i < crudo.length; i += 1) bytes[i] = crudo.charCodeAt(i)

  return bytes
}

/** Las llaves de la suscripción llegan como ArrayBuffer y se guardan en texto. */
function aTexto(buffer: ArrayBuffer | null): string {
  if (!buffer) return ''

  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

export type EstadoPush = {
  /** El navegador puede hacerlo. Safari en iOS solo si está instalada. */
  soportado: boolean
  /** Ya está recibiendo avisos en este teléfono. */
  activo: boolean
  /** Dijo que no. No se puede volver a preguntar desde la página. */
  bloqueado: boolean
  trabajando: boolean
  error: string | null
}

export function usePush() {
  const [estado, setEstado] = useState<EstadoPush>({
    soportado: false,
    activo: false,
    bloqueado: false,
    trabajando: false,
    error: null,
  })

  useEffect(() => {
    const soportado =
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window &&
      Boolean(LLAVE_PUBLICA)

    if (!soportado) {
      setEstado((previo) => ({ ...previo, soportado: false }))
      return
    }

    void (async () => {
      const registro = await navigator.serviceWorker.register('/sw.js')
      const suscripcion = await registro.pushManager.getSubscription()

      setEstado((previo) => ({
        ...previo,
        soportado: true,
        activo: Boolean(suscripcion),
        bloqueado: Notification.permission === 'denied',
      }))
    })()
  }, [])

  const activar = useCallback(async () => {
    setEstado((previo) => ({ ...previo, trabajando: true, error: null }))

    try {
      const permiso = await Notification.requestPermission()

      if (permiso !== 'granted') {
        setEstado((previo) => ({
          ...previo,
          trabajando: false,
          bloqueado: permiso === 'denied',
          error:
            permiso === 'denied'
              ? 'Le dijo que no al navegador. Hay que volver a permitirlo en los ajustes del sitio.'
              : null,
        }))
        return
      }

      const registro = await navigator.serviceWorker.ready

      const suscripcion = await registro.pushManager.subscribe({
        // Sin esto el navegador permite pushes sin contenido, y entonces la
        // notificación no podría decir de qué orden habla.
        userVisibleOnly: true,
        applicationServerKey: aBytes(LLAVE_PUBLICA!),
      })

      const { error } = await supabase.rpc('fn_registrar_push', {
        p_endpoint: suscripcion.endpoint,
        p_p256dh: aTexto(suscripcion.getKey('p256dh')),
        p_auth: aTexto(suscripcion.getKey('auth')),
        p_agente: navigator.userAgent.slice(0, 200),
      })

      if (error) throw new Error(mensajeDeError(error))

      setEstado((previo) => ({ ...previo, activo: true, trabajando: false }))
    } catch (fallo) {
      setEstado((previo) => ({
        ...previo,
        trabajando: false,
        error: fallo instanceof Error ? fallo.message : 'No se pudo activar.',
      }))
    }
  }, [])

  const apagar = useCallback(async () => {
    setEstado((previo) => ({ ...previo, trabajando: true, error: null }))

    try {
      const registro = await navigator.serviceWorker.ready
      const suscripcion = await registro.pushManager.getSubscription()

      if (suscripcion) {
        await supabase.rpc('fn_olvidar_push', { p_endpoint: suscripcion.endpoint })
        await suscripcion.unsubscribe()
      }

      setEstado((previo) => ({ ...previo, activo: false, trabajando: false }))
    } catch (fallo) {
      setEstado((previo) => ({
        ...previo,
        trabajando: false,
        error: fallo instanceof Error ? fallo.message : 'No se pudo apagar.',
      }))
    }
  }, [])

  return { estado, activar, apagar }
}
